import { fetchWithCsrfTokenRefresh } from 'bones/utils/portal/usePortalCsrfToken';

/**
 * returns in order of existence:
 * - json "body.message
 * - body as text
 * - http status number and status text
 */
export const getErrorMessage = async (
  response: Response,
  expectedStatusCodes?: Set<number>
): Promise<string> => {
  if (expectedStatusCodes && !expectedStatusCodes.has(response.status)) {
    return 'An error occurred. Please contact support@stardog.com if this error persists.';
  }

  let errorMessage = `${response.status} (${response.statusText})`;
  try {
    const text = await response.text();
    if (text) {
      errorMessage = text;
    }
    const obj = JSON.parse(text);
    if (obj?.message) {
      errorMessage = obj.message;
    }
  } catch {
    //
  }

  return errorMessage;
};

export const post = async <Body extends object | string, Result>(
  url: string,
  body: Body,
  expectedStatusCodes: Set<number>,
  {
    formatResponse = (response) => response.json(),
    headers,
  }: {
    formatResponse?: (response: Response) => Promise<Result>;
    headers?: Record<string, string>;
  } = {}
): Promise<
  | (Result & { error?: undefined })
  | ({ error: string; statusCode?: number } & {
      [K in keyof Result]?: undefined;
    })
> => {
  let statusCode: number | undefined;
  try {
    const response = await fetchWithCsrfTokenRefresh(url, {
      body:
        typeof body === 'string'
          ? body
          : JSON.stringify(
              Object.fromEntries(
                Object.entries(body).filter(([_, value]) => value !== undefined)
              )
            ),
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      method: 'POST',
    });
    if (response.ok) {
      const result = await formatResponse(response);
      return { ...result, error: undefined };
    }

    statusCode = response.status;

    const errorMessage = await getErrorMessage(response, expectedStatusCodes);
    throw new Error(errorMessage);
  } catch (err) {
    console.error(err);
    return { error: (err as Error).message, statusCode };
  }
};

export const createJsonLinesReader = <T>(
  body: ReadableStream<Uint8Array>
): (() => Promise<T | null>) => {
  const reader = body.getReader();
  const decoder = new TextDecoder('utf-8');

  let done = false;
  let parts = '';
  return async () => {
    if (done) {
      return null;
    }

    let result: T | null = null;
    try {
      do {
        // eslint-disable-next-line no-await-in-loop
        const partialResult = await reader.read();
        const chunk = decoder.decode(partialResult.value, {
          stream: !partialResult.done,
        });
        parts += chunk;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const idx = parts.indexOf('\n');
          if (idx === -1) {
            break;
          }
          const line = parts.slice(0, idx).trim();
          parts = parts.slice(idx + 1);
          if (line) {
            const jsonData: T = JSON.parse(line);
            result = jsonData;
          }
        }

        if (partialResult.done) {
          done = true;
        }
      } while (!done && !result);
    } catch (e) {
      console.error('Error reading JSON line', e);
      done = true;
    }

    if (done) {
      reader.releaseLock();
    }

    return result;
  };
};

const JSON_LINES_CONTENT_TYPES = new Set<string | null>([
  'application/x-ndjson',
  'application/jsonlines',
  'text/jsonlines',
]);
export const postJsonLines = async <
  Body extends object | string,
  ResultJson,
  Result
>(
  url: string,
  body: Body,
  expectedStatusCodes: Set<number>,
  {
    formatJsonResponse = (json) => json as any,
    headers,
  }: {
    formatJsonResponse?: (json: ResultJson) => Result;
    headers?: Record<string, string>;
  } = {}
): Promise<
  | { error?: undefined; next: () => Promise<Result | null> }
  | { error: string; statusCode?: number; next?: undefined }
> =>
  post<Body, { next: () => Promise<Result | null> }>(
    url,
    body,
    expectedStatusCodes,
    {
      formatResponse: async (response) => {
        const contentType = response.headers.get('Content-Type');
        if (response.body && JSON_LINES_CONTENT_TYPES.has(contentType)) {
          const readJsonLines = createJsonLinesReader<ResultJson>(
            response.body
          );
          return {
            next: async () => {
              const json = await readJsonLines();
              if (json) {
                return formatJsonResponse(json);
              }
              return null;
            },
          };
        }

        let done = false;
        return {
          next: async () => {
            if (!done) {
              done = true;
              const json = await response.json();
              return formatJsonResponse(json);
            }
            return null;
          },
        };
      },
      headers,
    }
  );
